import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Table, Dropdown, Button, Empty, Card, Avatar, Spin, message } from "antd";
import { MoreOutlined, DollarOutlined, TrophyOutlined, LineChartOutlined, EyeOutlined, FileProtectOutlined } from "@ant-design/icons";
import axios from "axios";
import { API_CONST } from "../../../const";
import {
  TalentsHiredContainer,
  PageHeader,
  MetricsContainer,
  TabsContainer,
  TableContainer,
  EmptyState,
} from "./TalentsHired.style";

const TalentsHired = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("Active Talents");
  const [hiringRecords, setHiringRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [myClientsOnly, setMyClientsOnly] = useState(
    () => sessionStorage.getItem("talentsHired_myClientsOnly") === "true"
  );

  const fetchHiringRecords = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("authToken");

      let customerOrganizations;
      if (myClientsOnly) {
        const relResponse = await axios.get(API_CONST.PARTNER_RELATION_MY_CLIENTS, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const activeClients = (relResponse.data.relations || []).filter(
          (r) => r.status === "active" && r.customerOrganization
        );
        if (activeClients.length === 0) {
          setHiringRecords([]);
          setLoading(false);
          return;
        }
        customerOrganizations = activeClients.map((r) => r.customerOrganization);
      }

      const body = {};
      if (customerOrganizations) body.customerOrganizations = customerOrganizations;

      const response = await axios.post(
        API_CONST.GET_HIRING_RECORDS,
        body,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setHiringRecords(response.data.hiringRecords || []);
    } catch (error) {
      console.error("Error fetching hiring records:", error);
      message.error("Failed to load hiring records");
    } finally {
      setLoading(false);
    }
  }, [myClientsOnly]);

  useEffect(() => {
    fetchHiringRecords();
  }, [fetchHiringRecords]);

  const handleMyClientsToggle = () => {
    const next = !myClientsOnly;
    setMyClientsOnly(next);
    sessionStorage.setItem("talentsHired_myClientsOnly", String(next));
  };

  const activeTalents = hiringRecords.filter((record) => record.status === "active");
  const inactiveTalents = hiringRecords.filter((record) => record.status !== "active");

  const currentData = activeTab === "Active Talents" ? activeTalents : inactiveTalents;

  const calculateMetrics = () => {
    const activeMonthlyBilling = activeTalents.reduce(
      (sum, record) => sum + (parseFloat(record.settledCost) || 0),
      0
    );
    const totalBilled = activeTalents.reduce(
      (sum, record) => sum + (parseFloat(record.totalBilled) || 0),
      0
    );
    const avgPerTalent =
      activeTalents.length > 0
        ? activeMonthlyBilling / activeTalents.length
        : 0;

    return {
      activeMonthlyBilling,
      totalBilled,
      avgPerTalent,
      activeTalentsCount: activeTalents.length,
    };
  };

  const metrics = calculateMetrics();

  const formatCurrency = (amount) => {
    if (amount >= 10000000) {
      return `₹${(amount / 10000000).toFixed(2)} Cr`;
    } else if (amount >= 100000) {
      return `₹${(amount / 100000).toFixed(2)} Lac`;
    } else {
      return `₹${amount.toLocaleString("en-IN")}`;
    }
  };

  const getDaysLeftClass = (days) => {
    if (days < 0) return "inactive";
    if (days <= 30) return "critical";
    if (days <= 90) return "warning";
    return "safe";
  };

  const columns = [
    {
      title: "Hiring ID",
      dataIndex: "id",
      key: "id",
      width: "10%",
      render: (id) => (
        <span style={{ color: "#014c75", fontWeight: 500 }}>{id}</span>
      ),
    },
    {
      title: "Job Id",
      dataIndex: "jobId",
      key: "jobId",
      width: "10%",
      render: (jobId) => (
        <a
          href="#"
          style={{ color: "#1890ff", fontWeight: 500 }}
          onClick={(e) => {
            e.preventDefault();
            navigate(`/partner/submit-profiles/${jobId}`);
          }}
        >
          {jobId}
        </a>
      ),
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: "12%",
      render: (text, record) => (
        <a
          href="#"
          style={{ color: "#1890ff", fontWeight: 500 }}
          onClick={(e) => {
            e.preventDefault();
            navigate(`/partner/talent-details/${record.talentId}`);
          }}
        >
          {text}
        </a>
      ),
    },
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
      width: "14%",
      render: (text) => <span className="role-text">{text}</span>,
    },
    {
      title: "Experience",
      dataIndex: "experience",
      key: "experience",
      width: "8%",
      render: (text) => <span>{text}</span>,
    },
    {
      title: "Monthly Rate",
      dataIndex: "monthlyRate",
      key: "monthlyRate",
      width: "9%",
      render: (rate) => (
        <span className="rate-text">₹ {parseFloat(rate || 0).toLocaleString("en-IN")}</span>
      ),
    },
    {
      title: "Settled Cost",
      dataIndex: "settledCost",
      key: "settledCost",
      width: "9%",
      render: (rate) => (
        <span className="rate-text">₹ {parseFloat(rate || 0).toLocaleString("en-IN")}</span>
      ),
    },
    {
      title: "Total Billed",
      dataIndex: "totalBilled",
      key: "totalBilled",
      width: "10%",
      render: (amount) => (
        <span className="billed-text" style={{ fontWeight: 600, color: "#014c75" }}>
          ₹ {parseFloat(amount || 0).toLocaleString("en-IN")}
        </span>
      ),
    },
    {
      title: "Onboarding Date",
      dataIndex: "onboardingDate",
      key: "onboardingDate",
      width: "10%",
      render: (date) => (
        <span className="date-text">
          {date ? new Date(date).toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
          }) : '-'}
        </span>
      ),
    },
    {
      title: "Contract",
      dataIndex: "contractDuration",
      key: "contractDuration",
      width: "8%",
      render: (duration) => <span className="duration-text">{duration}</span>,
    },
    {
      title: "Days Left",
      dataIndex: "daysLeft",
      key: "daysLeft",
      width: "9%",
      align: "center",
      render: (days) => (
        <span className={`days-left ${getDaysLeftClass(days)}`}>
          {days < 0 ? "Completed" : `${days} days`}
        </span>
      ),
    },
    {
      title: "Action",
      key: "action",
      width: "8%",
      align: "center",
      render: (_, record) => (
        <div className="action-buttons">
          <Dropdown
            menu={{
              items: [
                {
                  key: "view",
                  label: "View Details",
                  icon: <EyeOutlined />,
                  onClick: () => navigate(`/partner/talent-details/${record.talentId}`),
                },
                {
                  key: "compliance",
                  label: "Compliance Docs",
                  icon: <FileProtectOutlined />,
                  onClick: () => navigate("/partner/compliance-docs"),
                },
              ],
            }}
            trigger={["click"]}
          >
            <button className="icon-button menu-btn">
              <MoreOutlined />
            </button>
          </Dropdown>
        </div>
      ),
    },
  ];

  return (
    <TalentsHiredContainer>
      <PageHeader>
        <h1>Talents Hired</h1>
        <p>Track your hired talents and their assignments</p>
      </PageHeader>

      <MetricsContainer>
        <Card className="metric-card">
          <div className="metric-content">
            <Avatar
              size={64}
              icon={<DollarOutlined />}
              className="metric-icon"
              style={{ backgroundColor: "#e6fff9" }}
            />
            <div className="metric-info">
              <h3>{formatCurrency(metrics.activeMonthlyBilling)}</h3>
              <p>Active Monthly Billing</p>
            </div>
          </div>
        </Card>
        <Card className="metric-card">
          <div className="metric-content">
            <Avatar
              size={64}
              icon={<TrophyOutlined />}
              className="metric-icon"
              style={{ backgroundColor: "#f6ffed" }}
            />
            <div className="metric-info">
              <h3>{formatCurrency(metrics.totalBilled)}</h3>
              <p>Total Billed</p>
            </div>
          </div>
        </Card>
        <Card className="metric-card">
          <div className="metric-content">
            <Avatar
              size={64}
              icon={<LineChartOutlined />}
              className="metric-icon"
              style={{ backgroundColor: "#fff7e6" }}
            />
            <div className="metric-info">
              <h3>{formatCurrency(metrics.avgPerTalent)}</h3>
              <p>Avg per Talent</p>
            </div>
          </div>
        </Card>
      </MetricsContainer>

      <TabsContainer>
        <div style={{ display: "flex", gap: "8px" }}>
          <Button
            className={activeTab === "Active Talents" ? "tab-button active" : "tab-button"}
            onClick={() => setActiveTab("Active Talents")}
          >
            Active ({activeTalents.length})
          </Button>
          <Button
            className={activeTab === "Inactive Talents" ? "tab-button active" : "tab-button"}
            onClick={() => setActiveTab("Inactive Talents")}
          >
            Inactive ({inactiveTalents.length})
          </Button>
        </div>
        <Button
          type={myClientsOnly ? "primary" : "default"}
          onClick={handleMyClientsToggle}
          style={myClientsOnly ? { background: "#014c75", borderColor: "#014c75" } : {}}
        >
          My Clients Only
        </Button>
      </TabsContainer>

      <TableContainer>
        <Spin spinning={loading}>
          {currentData.length > 0 ? (
            <Table
              columns={columns}
              dataSource={currentData}
              rowKey="id"
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                pageSizeOptions: ["10", "20", "50", "100"],
                showTotal: (total) => `Total ${total} talents`,
              }}
              scroll={{ x: 1600 }}
            />
          ) : (
            <EmptyState>
              <Empty description={loading ? "Loading..." : `No ${activeTab.toLowerCase()} found`} />
            </EmptyState>
          )}
        </Spin>
      </TableContainer>
    </TalentsHiredContainer>
  );
};

export default TalentsHired;
